import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {
  createRegion,
  deleteRegion,
  getRegionTree,
  updateRegion,
  updateRegionStatus,
} from '../core/_requests'
import {ADMIN_PERMS, regionGates} from '../core/adminPermissions'
import type {RegionMutationRequest, RegionTreeNode} from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { getApiErrorMessage, showErrorToast } from '../../../shared/utils/apiErrorMessage'

type Props = {
  visible: boolean
  onClose: () => void
}

type FormState = {
  mode: 'create' | 'edit'
  targetId?: number | null
  name: string
  code: string
  type: 'CONTINENT' | 'COUNTRY' | 'CITY'
  parentId: string
  active: boolean
}

const EMPTY_FORM: FormState = {
  mode: 'create',
  targetId: null,
  name: '',
  code: '',
  type: 'CONTINENT',
  parentId: '',
  active: true,
}

const nextChildType = (type: 'CONTINENT' | 'COUNTRY' | 'CITY'): FormState['type'] | null => {
  if (type === 'CONTINENT') return 'COUNTRY'
  if (type === 'COUNTRY') return 'CITY'
  return null
}

const flattenTree = (nodes: RegionTreeNode[]): RegionTreeNode[] =>
  nodes.flatMap((node) => [node, ...flattenTree(node.children || [])])

const collectIds = (nodes: RegionTreeNode[]): number[] =>
  nodes.flatMap((node) => [node.id, ...collectIds(node.children || [])])

const matchesQuery = (node: RegionTreeNode, q: string) => {
  const haystack = `${node.name} ${node.code} ${node.path}`.toLowerCase()
  return haystack.includes(q)
}

const filterTree = (nodes: RegionTreeNode[], q: string): RegionTreeNode[] =>
  nodes
    .map((node) => {
      const children = filterTree(node.children || [], q)
      if (matchesQuery(node, q) || children.length > 0) {
        return {...node, children}
      }
      return null
    })
    .filter((node): node is RegionTreeNode => Boolean(node))

const highlight = (value: string, query: string) => {
  if (!query.trim()) return value
  const lower = value.toLowerCase()
  const needle = query.toLowerCase()
  const index = lower.indexOf(needle)
  if (index < 0) return value
  return (
    <>
      {value.slice(0, index)}
      <mark className='px-1'>{value.slice(index, index + needle.length)}</mark>
      {value.slice(index + needle.length)}
    </>
  )
}

const RegionLibraryModal = ({visible, onClose}: Props) => {
  const {hasPermission, isSuperAdmin} = useAuth()
  const gates = useMemo(() => regionGates(hasPermission, isSuperAdmin), [hasPermission, isSuperAdmin])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [tree, setTree] = useState<RegionTreeNode[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  const allNodes = useMemo(() => flattenTree(tree), [tree])
  const selectedNode = useMemo(
    () => allNodes.find((node) => node.id === selectedId) || null,
    [allNodes, selectedId]
  )
  const visibleTree = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    return q ? filterTree(tree, q) : tree
  }, [tree, searchTerm])
  const parentOptions = useMemo(() => {
    if (form.type === 'CONTINENT') return []
    const requiredParentType = form.type === 'COUNTRY' ? 'CONTINENT' : 'COUNTRY'
    return allNodes.filter(
      (node) =>
        node.type === requiredParentType &&
        node.active !== false &&
        node.id !== form.targetId
    )
  }, [allNodes, form.targetId, form.type])

  const loadTree = async () => {
    if (!visible || !gates.canRead) return
    setLoading(true)
    setError(null)
    try {
      const rows = await getRegionTree(true)
      setTree(rows)
      setExpanded((prev) => (prev.size > 0 ? prev : new Set(collectIds(rows))))
      setSelectedId((prev) => (prev != null ? prev : rows[0]?.id ?? null))
    } catch (e: any) {
      setError('Failed to load regions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!visible) return
    void loadTree()
  }, [visible, gates.canRead])

  useEffect(() => {
    if (!selectedNode || form.mode === 'create') return
    setForm({
      mode: 'edit',
      targetId: selectedNode.id,
      name: selectedNode.name || '',
      code: selectedNode.code || '',
      type: selectedNode.type,
      parentId: selectedNode.parentId ? String(selectedNode.parentId) : '',
      active: selectedNode.active !== false,
    })
  }, [selectedNode, form.mode])

  const beginCreate = (preset?: Partial<FormState>) => {
    setForm({
      ...EMPTY_FORM,
      ...preset,
      mode: 'create',
      targetId: null,
      active: true,
    })
  }

  const beginEdit = (node: RegionTreeNode) => {
    setSelectedId(node.id)
    setForm({
      mode: 'edit',
      targetId: node.id,
      name: node.name || '',
      code: node.code || '',
      type: node.type,
      parentId: node.parentId ? String(node.parentId) : '',
      active: node.active !== false,
    })
  }

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSubmit = async () => {
    const payload: RegionMutationRequest = {
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type,
      parentId: form.parentId ? Number(form.parentId) : null,
      active: form.active,
    }
    setSubmitting(true)
    setFormError(null)
    try {
      if (form.mode === 'create') {
        await createRegion(payload)
        toast.success('Region created successfully.')
      } else if (form.targetId != null) {
        await updateRegion(form.targetId, payload)
        toast.success('Region updated successfully.')
      }
      window.dispatchEvent(new Event('region-library-updated'))
      await loadTree()
    } catch (e: any) {
      console.error('Failed to save region:', e)
      const serverMsg = getApiErrorMessage(
        e,
        form.mode === 'create' ? 'Failed to create region.' : 'Failed to update region.',
        form.mode === 'create' ? 'create' : 'update'
      )
      setFormError(serverMsg)
      toast.error(serverMsg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleStatus = async (node: RegionTreeNode) => {
    if (!gates.canUpdate) return
    setSubmitting(true)
    try {
      await updateRegionStatus(node.id, node.active === false)
      toast.success(node.active === false ? 'Region activated.' : 'Region deactivated.')
      window.dispatchEvent(new Event('region-library-updated'))
      await loadTree()
    } catch (e: any) {
      console.error('Failed to update region status:', e)
      showErrorToast(e, 'Failed to update region status.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (node: RegionTreeNode) => {
    if (!gates.canDelete) return
    if (!window.confirm(`Delete region "${node.path}"?`)) return
    setSubmitting(true)
    try {
      await deleteRegion(node.id)
      toast.success('Region deleted successfully.')
      window.dispatchEvent(new Event('region-library-updated'))
      setSelectedId(null)
      beginCreate()
      await loadTree()
    } catch (e: any) {
      console.error('Failed to delete region:', e)
      showErrorToast(e, 'Failed to delete region. It may have sub-regions or be associated with vendors.', 'delete')
    } finally {
      setSubmitting(false)
    }
  }

  const renderNode = (node: RegionTreeNode, level = 0): React.ReactNode => {
    const hasChildren = (node.children || []).length > 0
    const isExpanded = expanded.has(node.id)
    const childType = nextChildType(node.type)
    return (
      <div key={node.id}>
        <div
          className={`d-flex align-items-start justify-content-between gap-2 rounded px-2 py-2 mb-1 ${
            selectedId === node.id ? 'bg-light-primary border border-primary' : 'border border-transparent'
          }`}
          style={{marginLeft: `${level * 18}px`, cursor: 'pointer'}}
          onClick={() => {
            setSelectedId(node.id)
            beginEdit(node)
          }}
        >
          <div className='d-flex align-items-start gap-2 flex-grow-1'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light mt-1'
              style={{visibility: hasChildren ? 'visible' : 'hidden'}}
              onClick={(event) => {
                event.stopPropagation()
                toggleExpanded(node.id)
              }}
            >
              <span className='fs-7'>{isExpanded ? '-' : '+'}</span>
            </button>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span className='fw-bold text-gray-900'>{highlight(node.name, searchTerm)}</span>
                <span className='badge bg-light text-gray-700 border'>{node.type}</span>
                <span className={`badge ${node.active === false ? 'bg-light-danger text-danger' : 'bg-light-success text-success'} border`}>
                  {node.active === false ? 'Inactive' : 'Active'}
                </span>
              </div>
              <div className='text-muted fs-8'>{highlight(node.path, searchTerm)}</div>
              <div className='text-muted fs-8'>Code: {highlight(node.code, searchTerm)}</div>
            </div>
          </div>
          <div className='d-flex gap-1'>
            {gates.canCreate && childType && (
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={(event) => {
                  event.stopPropagation()
                  setSelectedId(node.id)
                  beginCreate({type: childType, parentId: String(node.id)})
                  setExpanded((prev) => new Set(prev).add(node.id))
                }}
              >
                Add Child
              </button>
            )}
            {gates.canUpdate && (
              <button
                type='button'
                className='btn btn-sm btn-light-warning'
                onClick={(event) => {
                  event.stopPropagation()
                  handleToggleStatus(node)
                }}
              >
                {node.active === false ? 'Activate' : 'Deactivate'}
              </button>
            )}
            {gates.canDelete && (
              <button
                type='button'
                className='btn btn-sm btn-light-danger'
                onClick={(event) => {
                  event.stopPropagation()
                  handleDelete(node)
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && node.children.map((child) => renderNode(child, level + 1))}
      </div>
    )
  }

  if (!visible) return null

  return (
    <div className='modal fade show d-block' tabIndex={-1} role='dialog' style={{backgroundColor: 'rgba(0, 0, 0, 0.45)'}}>
      <div className='modal-dialog modal-dialog-centered modal-xl' role='document' style={{maxWidth: '1200px'}}>
        <div className='modal-content' style={{maxHeight: '92vh'}}>
          <div className='modal-header'>
            <div>
              <h3 className='modal-title mb-1'>Region Library</h3>
              <div className='text-muted fs-7'>Global reference data for continent, country, and city coverage.</div>
            </div>
            <button type='button' className='btn btn-sm btn-icon btn-light' onClick={onClose}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </div>

          <div className='modal-body overflow-auto'>
            {!gates.canRead ? (
              <div className='alert alert-warning mb-0'>
                You do not have permission to view the Region Library. Required permission: {ADMIN_PERMS.READ_REGION}.
              </div>
            ) : (
              <div className='row g-4'>
                <div className='col-lg-7'>
                  <div className='border rounded p-3 h-100'>
                    <div className='d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3'>
                      <div className='position-relative flex-grow-1' style={{minWidth: '260px'}}>
                        <input
                          className='form-control'
                          placeholder='Search by name, code, or path'
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setExpanded(new Set(collectIds(tree)))}
                        >
                          Expand All
                        </button>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => setExpanded(new Set())}
                        >
                          Collapse All
                        </button>
                        {gates.canCreate && (
                          <button
                            type='button'
                            className='btn btn_primary'
                            onClick={() => beginCreate({type: 'CONTINENT', parentId: ''})}
                          >
                            Add Root
                          </button>
                        )}
                      </div>
                    </div>

                    {loading ? <div className='text-muted'>Loading regions...</div> : null}
                    {!loading && error ? <div className='alert alert-danger mb-0'>{error}</div> : null}
                    {!loading && !error && visibleTree.length === 0 ? (
                      <div className='text-muted'>{searchTerm.trim() ? 'No regions match your search.' : 'No regions available.'}</div>
                    ) : null}
                    {!loading && !error && visibleTree.length > 0 ? (
                      <div>{visibleTree.map((node) => renderNode(node))}</div>
                    ) : null}
                  </div>
                </div>

                <div className='col-lg-5'>
                  <div className='border rounded p-3 h-100'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h5 className='mb-0'>{form.mode === 'create' ? 'Create Region' : 'Edit Region'}</h5>
                      {form.mode === 'edit' && selectedNode ? (
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => beginCreate({type: selectedNode.type, parentId: selectedNode.parentId ? String(selectedNode.parentId) : ''})}
                        >
                          Duplicate as New
                        </button>
                      ) : null}
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='region-form-type' className='form-label'>Type</label>
                      <SearchableSelect
                        id='region-form-type'
                        className='form-select'
                        value={form.type}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            type: e.target.value as FormState['type'],
                            parentId: e.target.value === 'CONTINENT' ? '' : prev.parentId,
                          }))
                        }
                        disabled={form.mode === 'edit'}
                      >
                        <option value='CONTINENT'>Continent</option>
                        <option value='COUNTRY'>Country</option>
                        <option value='CITY'>City</option>
                      </SearchableSelect>
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='region-form-name' className='form-label'>Name</label>
                      <input
                        id='region-form-name'
                        className='form-control'
                        value={form.name}
                        onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                      />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='region-form-code' className='form-label'>Code</label>
                      <input
                        id='region-form-code'
                        className='form-control'
                        value={form.code}
                        onChange={(e) => setForm((prev) => ({...prev, code: e.target.value.toUpperCase()}))}
                      />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='region-form-parent' className='form-label'>Parent</label>
                      <SearchableSelect
                        id='region-form-parent'
                        className='form-select'
                        value={form.parentId}
                        onChange={(e) => setForm((prev) => ({...prev, parentId: e.target.value}))}
                        disabled={form.type === 'CONTINENT'}
                      >
                        <option value=''>{form.type === 'CONTINENT' ? 'No parent' : 'Select parent'}</option>
                        {parentOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.path}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>

                    <div className='form-check form-switch mb-4'>
                      <input
                        id='region-form-active'
                        className='form-check-input'
                        type='checkbox'
                        checked={form.active}
                        onChange={(e) => setForm((prev) => ({...prev, active: e.target.checked}))}
                      />
                      <label htmlFor='region-form-active' className='form-check-label'>
                        Active for new vendor and RFQ selections
                      </label>
                    </div>

                    {formError && (
                      <div className='alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-3'>
                        <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-1 svg-icon-danger' />
                        <span className='fs-7'>{formError}</span>
                      </div>
                    )}

                    <div className='d-flex gap-2'>
                      {(form.mode === 'create' ? gates.canCreate : gates.canUpdate) && (
                        <button
                          type='button'
                          className='btn btn_primary'
                          disabled={submitting}
                          onClick={() => void handleSubmit()}
                        >
                          {submitting ? 'Saving...' : form.mode === 'create' ? 'Create Region' : 'Save Changes'}
                        </button>
                      )}
                      <button type='button' className='btn btn-light' onClick={() => { beginCreate(); setFormError(null) }}>
                        Reset
                      </button>
                    </div>

                    {selectedNode ? (
                      <div className='mt-4 p-3 bg-light rounded'>
                        <div className='fw-bold mb-1'>Selected Path</div>
                        <div className='text-gray-800'>{selectedNode.path}</div>
                        <div className='text-muted fs-8 mt-1'>
                          Historical inactive regions remain viewable but are excluded from new vendor and RFQ picks.
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegionLibraryModal
